import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import FormTeamSelection from "../../components/BookingForm/FormTeamSelection/form-team-selection.component";
import FormConfirm from "../../components/BookingForm/form-confirm.component";
import FormDateTime from "../../components/BookingForm/form-date-time.component";
import FormLocation from "../../components/BookingForm/form-location.component";
import FormSelection from "../../components/BookingForm/form-selection.component";
import FormSuccess from "../../components/BookingForm/form-success.component";
import FormWorkplace from "../../components/BookingForm/form-workplace.component";
import FormZoneRestriction from "../../components/BookingForm/form-zone-restriction.component";
import {
  checkModeVehicle,
  checkModeVisitor,
  placeMode,
  placeModeExcludeVehicle
} from "./functions/booking-form.functions";
import { clearFloors, clearInputs, selectBookingLocation, setInputs } from "./slices/booking.slice";
import { LocationInputs } from "./typings/inputs-location";
import { visitorRequestsPlaceOrRoom } from "./functions/visitor-registration.functions";
import { BookingType } from "./typings/booking-inputs";
import { FormVehicleSelection } from "../../components/BookingForm/form-vehicle-selection.component";
import { FormVisitorRegistration } from "../../components/BookingForm/form-visitor-registration.component";

interface OwnProps {
  step: string;
  nextStep: (to?: number) => void;
  setValidDates: React.Dispatch<React.SetStateAction<boolean>>;
  validDates: boolean;
  adjustedData: any;
}

type Props = OwnProps;

const StepContent: FunctionComponent<Props> = ({
  step,
  nextStep,
  adjustedData,
  setValidDates,
  validDates
}) => {
  const dispatch = useDispatch();
  const { inputs } = useSelector((state: RootState) => state.booking);
  const { t } = useTranslation();

  const errorToState = (error: string) => dispatch(setInputs({ errorConfirm: error }));

  const locationToState = (inp: LocationInputs) => _locationToState(inp, dispatch);

  const clearState = () => {
    dispatch(clearInputs());
    dispatch(clearFloors());
  };

  const step1 = () => {
    if (inputs.mode === BookingType.PLACEZONE) {
      return (
        <FormZoneRestriction
          nextStep={nextStep}
          adjustedData={adjustedData}
          title={t("Who should have access to your zone?")}
        />
      );
    }

    return <FormDateTime setValidDates={setValidDates} validDate={validDates} />;
  };

  const step2 = () => {
    // either WORKPLACE, PARKINGPLACE, or ELECTRICCHARGINGSTATIONPLACE
    if (placeModeExcludeVehicle(inputs))
      return <FormLocation nextStep={nextStep} locationToState={locationToState} />;

    // VEHICLE
    if (checkModeVehicle(inputs)) return <FormVehicleSelection nextStep={nextStep} />;

    if (inputs.mode === BookingType.CONFERENCEZONE) {
      return (
        <FormZoneRestriction
          nextStep={nextStep}
          adjustedData={adjustedData}
          title={t("Who should have access to your zone?")}
        />
      );
    }

    // VISITOR — registration (dedicated mode, not Team)
    if (checkModeVisitor(inputs)) return <FormVisitorRegistration />;

    // CONFERENCEZONE
    if (inputs.mode === BookingType.PLACEZONE) {
      return <FormDateTime setValidDates={setValidDates} validDate={validDates} />;
    }
    // TEAM
    return <FormTeamSelection data-testid="team-selection" title={t("Select your team members")} />;
  };

  const step3 = () => {
    // WORKPLACE, PARKINGPLACE, ELECTRICCHARGINGSTATIONPLACE
    if (placeModeExcludeVehicle(inputs)) return <FormWorkplace nextStep={nextStep} />;
    // VEHICLE
    if (checkModeVehicle(inputs)) return <FormConfirm errorToState={errorToState} />;
    // VISITOR
    if (checkModeVisitor(inputs)) {
      return visitorRequestsPlaceOrRoom(inputs) ? (
        <FormLocation nextStep={nextStep} locationToState={locationToState} />
      ) : (
        <FormConfirm errorToState={errorToState} />
      );
    }
    // CONFERENCEZONE / TEAM (location step)
    return <FormLocation nextStep={nextStep} locationToState={locationToState} />;
  };

  const step4 = () => {
    const isTeamMobileWorking = inputs.mode === BookingType.TEAM && inputs.isMobileWorking;
    const visitorNeedsWorkplaceStep =
      checkModeVisitor(inputs) && visitorRequestsPlaceOrRoom(inputs);
    const shouldRenderConfirmStep =
      (placeMode(inputs) || isTeamMobileWorking) && !visitorNeedsWorkplaceStep;

    if (shouldRenderConfirmStep) {
      return <FormConfirm errorToState={errorToState} />;
    }

    return <FormWorkplace nextStep={nextStep} />;
  };

  const shouldShowSuccessOnStep5 = placeMode(inputs) && !checkModeVisitor(inputs);
  const step5 = shouldShowSuccessOnStep5 ? (
    <FormSuccess />
  ) : (
    <FormConfirm errorToState={errorToState} />
  );

  return (
    <>
      {
        {
          "0": <FormSelection nextStep={nextStep} changeAction={clearState} />,
          "1": step1(),
          "2": step2(),
          "3": step3(),
          "4": step4(),
          "5": step5,
          "6": <FormSuccess />
        }[step]
      }
    </>
  );
};

export default StepContent;

const _locationToState = (
  {
    selectedLocation,
    locationName,
    isMobileWorking,
    timezone,
    automatedSeating,
    automatedSeatingObjects
  }: LocationInputs,
  dispatch: ReturnType<typeof useDispatch>
) => {
  dispatch(
    selectBookingLocation({
      selectedLocation: selectedLocation ?? null,
      locationName: locationName || null,
      isMobileWorking: isMobileWorking || false,
      timezone,
      automatedSeating,
      automatedSeatingObjects
    })
  );
};
